import { huntWound } from './hunt.ts';
import { aegisBroke, formsDown, formsHoldUp, formsStruck, holyTakenBp, watchStruckBack } from './forms.ts';
import {
  AILMENT_MARKS,
  BLOCK_TAKEN_BP,
  MAZE,
  DUNGEON,
  ELITE_HP_BP,
  ENCHANT_FX,
  IRON_DAMAGE_TAKEN_BP,
  MATERIAL_RESIST,
  FORKS,
  GEAR_EFFECT_FX,
  NIGHTMARES,
  RELIC_AWAKENED_FX,
  RELIC_FX,
  RESPAWN_TICKS,
  RESPAWN_TICKS_RING,
  SAGA,
  SEASON_BY_ID,
  STRUCTURES,
  TIERS,
  TITAN_STONE,
  UNIQUES,
  VELA,
  REACTIONS,
  type AilmentKind,
  type DamageType,
  type EnemyKind,
} from '@somnhold/content';
import { reflect } from './obsidian.ts';
import { dragonTakes } from './dragon.ts';
import { callSpared } from './call.ts';
import { trapShield } from './trap.ts';
import { blastHit } from './blast.ts';
import { risingWeak } from './rising.ts';
import { drinkerTakenBp } from './drinker.ts';
import { fairLit } from './fair.ts';
import { mawTakenBp } from './maw.ts';
import { sunkenTakenBp } from './sunken.ts';
import { unsleepingGuard, unsleepingHit, unsleepingMarkStrike, unsleepingTakes, unsleepingUntouchable } from './unsleeping.ts';
import { facetakerTakes } from './facetaker.ts';
import { awake } from './gear.ts';
import { bruniBp, corvinGuard, eddaHolds, relicBlow, relicStruck } from './relics.ts';
import { cellCenter, touchGrid } from '../grid.ts';
import { hexDist, hexToWorld, worldToCell } from '../hex.ts';
import { mapEra } from '../walls.ts';
import { ANGLE_STEPS, cosA, dist2, iatan2, idiv, imax, imin, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { featAegis, featSaved, featThorns, featWard } from './feats.ts';
import { rngChance, rngInt, rngRange } from '../math/rng.ts';
import type { BattleEvent, BattleState, Enemy, Hero, PlayerId, PlayerStats, Structure } from '../types.ts';
import { emptyPlayerStats, heroHome } from './create.ts';
import { shapeEnemy } from './shapes.ts';
import { devotionBp, keepGuardBp, rampartBp, retribution, soak, templeGuardBp } from './kits.ts';
import { droneCoverBp } from './bots.ts';
import { simaBlow, simaSpawned, simaTakes } from './sima.ts';
import { simaBossHit, simaBossTakes } from './sima-bosses.ts';
import { spotted } from './vigil.ts';

export const ev = (s: BattleState, e: BattleEvent): void => {
  (s._events ??= []).push(e);
};

/** A hero calls the nightmare onto itself for `ticks`; a siege engine pays it no mind. */
export const taunt = (e: Enemy, h: Hero, ticks: number): void => {
  if (NIGHTMARES[e.kind].siege) return;
  e.taunter = h.id;
  e.tauntTicks = ticks;
};

export const pstats = (s: BattleState, pid: PlayerId): PlayerStats => {
  let p = s.stats.players[pid];
  if (!p) {
    p = emptyPlayerStats();
    s.stats.players[pid] = p;
  }
  return p;
};

export const KEEP_LIGHT = 2500;

/**
 * The beacons among the structures, with their centers, so a check for light doesn't walk the whole
 * ring wall. The list grows by push and is replaced when something is removed, so its identity and
 * length tell whether this is current (a beacon that falls is still skipped by `destroyed`).
 */
const BEACONS = new WeakMap<Structure[], { n: number; list: { st: Structure; x: number; y: number }[] }>();
export const beacons = (s: BattleState): { st: Structure; x: number; y: number }[] => {
  const m = BEACONS.get(s.structures);
  if (m && m.n === s.structures.length) return m.list;
  const list = s.structures.filter((st) => st.type === 'beacon').map((st) => ({ st, ...hexToWorld(st.q, st.r) }));
  BEACONS.set(s.structures, { n: s.structures.length, list });
  return list;
};

/** Invisible nightmares can only be targeted inside a light radius. */
export const isRevealed = (s: BattleState, e: Enemy): boolean => {
  const dark = s.rules?.dark;
  if (!dark) {
    if (!NIGHTMARES[e.kind].invisible) return true;
  } else {
    // In the dark (a bet): only the node's brazier and the heroes' lanterns show anything.
    if (withinRange(e.x, e.y, 0, 0, KEEP_LIGHT)) return true;
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, dark)) return true;
    if (!NIGHTMARES[e.kind].invisible) return false;
  }
  // The crypt has no beacons: stalkers show only in the torchlight around a standing hero.
  if (s.mode === 'dungeon') return s.heroes.some((h) => h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, DUNGEON.torchRange));
  // Fair day: the shut well has no brazier; only the stalls' lanterns and the heroes' light the square.
  if (!s.rules?.fair && withinRange(e.x, e.y, 0, 0, KEEP_LIGHT)) return true;
  for (const b of beacons(s)) {
    if (!b.st.destroyed && withinRange(e.x, e.y, b.x, b.y, STRUCTURES.beacon.revealRange ?? 0)) return true;
  }
  // The watchtower's eye (era IV).
  if (spotted(s, e.x, e.y)) return true;
  // The saga's caravan carries lanterns.
  const cart = s.escort ? s.bots?.find((b) => b.id === s.escort?.cart) : undefined;
  if (cart && withinRange(e.x, e.y, cart.x, cart.y, SAGA.escort.lantern)) return true;
  // The moss whistle's rain shows what hides under it.
  for (const z of s.zones) if (z.reveal && withinRange(e.x, e.y, z.x, z.y, z.radius)) return true;
  // Fair day: the stalls' lanterns and the heroes' own show the ink thieves.
  if (s.rules?.fair && fairLit(s, e.x, e.y)) return true;
  // A hawk's sharp eye.
  for (const b of s.bots ?? []) if (b.reveal && withinRange(e.x, e.y, b.x, b.y, b.reveal)) return true;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !h.relics.includes('vigilLantern')) continue;
    if (withinRange(e.x, e.y, h.x, h.y, awake(h, 'vigilLantern') ? RELIC_AWAKENED_FX.vigilLantern.revealRange : RELIC_FX.vigilLantern.revealRange)) return true;
  }
  return false;
};

/** An invisible nightmare in the light of an awakened vigil lantern: it takes more. */
const lanternMarked = (s: BattleState, e: Enemy): boolean => {
  if (!NIGHTMARES[e.kind].invisible) return false;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !awake(h, 'vigilLantern')) continue;
    if (withinRange(e.x, e.y, h.x, h.y, RELIC_AWAKENED_FX.vigilLantern.revealRange)) return true;
  }
  return false;
};

/** Flying, unless roots hold it down for now (druid). */
export const isFlying = (e: Enemy): boolean => (NIGHTMARES[e.kind].flying || !!e.air) && !(e.grounded && e.grounded > 0);

/** Targetable by player attacks. */
/** A sapper or a shell serpent under the ground (phase 1) can't be reached, unless a watchtower marks it. */
export const targetable = (s: BattleState, e: Enemy): boolean =>
  e.hp > 0 && !(e.phase === 1 && (NIGHTMARES[e.kind].burrows || NIGHTMARES[e.kind].tunnels) && !spotted(s, e.x, e.y)) && !(e.kind === 'marrowWorm' && e.phase === 7) && isRevealed(s, e);

export interface DamageOpts {
  pid: PlayerId;
  /** Bolts and arrows: subject to pierce armor. */
  pierce?: boolean;
  ignoresArmor?: boolean;
  /** A hero's critical (it says so on the hit). */
  crit?: boolean;
  /** Its damage type (absent: physical). */
  type?: DamageType;
  /** A reaction's own damage: it sets off no other. */
  noReact?: boolean;
  /** The old trap's harpoon (La Quemadura 3): the one blow its colossus doesn't shrug off. */
  harpoon?: boolean;
  /** Passed along shadow chains: it goes no further. */
  linked?: boolean;
  /** A burn or a poison ticking on it (the dragon: a burn never counts as fire thrown at it). */
  dot?: boolean;
  /** A shatter's shards (frost that never feeds the dragon's cold). */
  shard?: boolean;
  /** A turret's bolt (a dry blow on the dragon's glass). */
  bolt?: boolean;
  /** Where the shot came from (a sentinel's shield stops those from the front). */
  fromX?: number;
  fromY?: number;
}

/** A resistance in bp applied to a blow (negative: weak to it); never below 1 unless it is fully immune. */
const resisted = (dmg: number, bp: number): number => (bp === 0 ? dmg : bp >= 10000 ? 0 : mulBp(dmg, 10000 - bp));

export const damageEnemy = (s: BattleState, e: Enemy, amount: number, o: DamageOpts): number => {
  if (e.hp <= 0 || amount <= 0) return 0;
  if (e.kind === 'warden' && (s.dungeon?.roarUntil ?? 0) > s.tick) return 0;
  // The marrow worm under the ground: nothing reaches it.
  if (e.kind === 'marrowWorm' && e.phase === 7) return 0;
  // A scared prowler is already on its way out: nothing more lands until it is gone.
  if (e.kind === 'prowler' && e.scared !== undefined) return 0;
  // An Unsleeping untouchable a moment (Edda's broken oath).
  if (e.un && unsleepingUntouchable(e)) return 0;
  // Ragna kneeling at the clan's call.
  if (s.call && callSpared(s, e.id)) return 0;
  // The dragon of the Burn: its scales, the glass's cold, its dry blows; fire only closes them.
  if (s.dragon && e.id === s.dragon.id) {
    amount = dragonTakes(s, e, amount, o);
    if (amount <= 0) return 0;
  }
  const armor = o.ignoresArmor || !o.pierce ? 0 : NIGHTMARES[e.kind].pierceArmorBp;
  let dmg = mulBp(trapShield(s, e.id, amount, !!o.harpoon), 10000 - armor);
  // Its type against what this nightmare resists or fears (docs/CRIATURAS.md §3).
  const type = o.type ?? 'physical';
  dmg = resisted(dmg, NIGHTMARES[e.kind].resist?.[type] ?? 0);
  // Any fire stops a mender knitting back (a burn, a burning zone, a combustion): enemies.ts reads it.
  if (type === 'fire' && NIGHTMARES[e.kind].regen) e.scorch = s.tick;
  // Fire panics an ash mammoth, unless it calmed down lately.
  const spooks = NIGHTMARES[e.kind].spooks;
  if (spooks && type === 'fire' && s.tick >= (e.calm ?? 0)) {
    e.panic = s.tick + spooks.ticks;
    e.calm = e.panic + spooks.immune;
    e.next = -1;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: spooks.radius, kind: 'panic' });
  }
  // A beetle's shell: light blows glance off until a heavy one, a critical or a stun cracks it.
  const shell = NIGHTMARES[e.kind].shell;
  if (shell && !e.cracked) {
    e.shellWear = (e.shellWear ?? 0) + amount;
    if (amount >= shell.breakAt || o.crit || e.stun > 0 || e.shellWear >= shell.wear) {
      e.cracked = true;
      ev(s, { t: 'boom', x: e.x, y: e.y, r: 600, kind: 'crack' });
    } else dmg = mulBp(dmg, shell.takenBp);
  }
  // A fallen sentinel's tower shield: a shot from in front of it lands at a fraction.
  const bulwark = NIGHTMARES[e.kind].bulwark;
  if (bulwark && o.fromX !== undefined && o.fromY !== undefined && e.stun <= 0) {
    const d = (((iatan2(o.fromY - e.y, o.fromX - e.x) - e.facing) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS - ANGLE_STEPS / 2;
    if (d <= bulwark.arc && d >= -bulwark.arc) {
      dmg = mulBp(dmg, bulwark.takenBp);
      if (s.tick % 6 === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'blocked' });
    }
  }
  // A glass drake's scales: a light blow barely scratches the glass (a critical goes through, like the dragon's).
  const glass = NIGHTMARES[e.kind].glassScales;
  if (glass && amount < glass.breakAt && !o.crit) dmg = mulBp(dmg, glass.takenBp);
  // A shellback's plates: what is shot at it from afar lands at a fraction.
  const plated = NIGHTMARES[e.kind].plated;
  if (plated && o.fromX !== undefined) dmg = mulBp(dmg, plated.takenBp);
  // Eaten by acid: it takes more from everything.
  if (e.acidTicks && e.acidTicks > 0) dmg = mulBp(dmg, ENCHANT_FX.acid.vulnBp);
  // Marked by the watchtower bow.
  if (e.markTicks && e.markTicks > 0 && e.markBp) dmg = mulBp(dmg, e.markBp);
  // Smitten by the heavy hammer (Ser Gaspar's, docs/UNICAS.md §3): it takes more holy while it stays stunned.
  if (e.holyT !== undefined && type === 'holy') dmg = mulBp(dmg, holyTakenBp(s, e));
  // Caught in an awakened lantern's light.
  if (lanternMarked(s, e)) dmg = mulBp(dmg, 10000 + RELIC_AWAKENED_FX.vigilLantern.revealedTakenBp);
  // La Orilla 3: the Maw shrugs off most blows while its light shines.
  if (e.kind === 'maw') dmg = mulBp(dmg, mawTakenBp(s));
  // La Orilla 5: the Drowned Dragon's bones shrug off most blows (the fin is what breaks them).
  if (e.kind === 'sunken') dmg = mulBp(dmg, sunkenTakenBp(s));
  // El pozo 2: the Drinker swollen takes double.
  if (e.kind === 'drinker') dmg = mulBp(dmg, drinkerTakenBp(s, e));
  // Las vías 4: under Bruni's right arm, where he never covered himself, every blow is a critical.
  const weak = !!s.rules?.rising && !!o.pid && risingWeak(s, e, o.pid);
  if (weak) dmg = mulBp(dmg, s.rules?.rising?.weakBp ?? 10000);
  // A fallen star's light in the hand.
  if (s.rules?.stars && o.pid) {
    const h = s.heroes.find((x) => x.pid === o.pid);
    if (h && (h.starUntil ?? 0) > s.tick) dmg = mulBp(dmg, s.rules.stars.dmgBp);
  }
  // Ragna's horn: the hero hits harder the more it has bled.
  if (o.pid) dmg = relicBlow(s, o.pid, dmg);
  // The Prestacaras under a lantern: no guard, and blows land harder.
  if (e.mask && e.mask.bare > 0) dmg = facetakerTakes(e, dmg);
  // One of the Unsleeping: his guard from the front, and exposed after a cut channel.
  else if (e.un) dmg = unsleepingTakes(s, e, dmg, o.pid, o.fromX, o.fromY);
  // Another nightmare under an Unsleeping's guard (Edda's oath).
  else if (s.un) dmg = unsleepingGuard(s, e, dmg, o.pid);
  // The Chasm: the ward sign, brittle molten metal, a guardian under its rule.
  if (s.dungeon?.sima) dmg = simaBossTakes(s, e, simaTakes(s, e, dmg, type));
  if (dmg < 1) dmg = 1;
  // Ansel's ash soaks every blow until it is spent.
  if (e.ash && e.ash > 0) {
    const soaked = imin(e.ash, dmg);
    e.ash -= soaked;
    dmg -= soaked;
    if (e.ash === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'anselAshGone' });
    if (dmg <= 0) {
      e.lastHit = s.tick;
      return 0;
    }
  }
  // An ink oracle's bubble soaks the light blows; a heavy one, a critical or a stun bursts it and lands whole.
  if (e.ward && e.ward > 0) {
    if (amount >= (NIGHTMARES.oracle.wards?.breakAt ?? 0) || o.crit || e.stun > 0) {
      e.ward = 0;
      ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'wardBreak' });
    } else {
      const soaked = imin(e.ward, dmg);
      e.ward -= soaked;
      dmg -= soaked;
      if (e.ward === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'wardBreak' });
      if (dmg <= 0) {
        e.lastHit = s.tick;
        return 0;
      }
    }
  }
  // The Furrows' prowler never falls: a full bar of blows scares it off a while (battle/maze.ts sends it away).
  if (e.kind === 'prowler' && dmg >= e.hp) {
    e.hp = e.maxHp;
    e.stun = imax(e.stun, MAZE.prowler.staggerTicks);
    e.scared = s.tick;
    e.lastHit = s.tick;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 700, kind: 'prowlerScared' });
    return dmg;
  }
  if (dmg > e.hp) dmg = e.hp;
  e.hp -= dmg;
  e.lastHit = s.tick;
  if (e.kind === 'uro') huntWound(s, o.pid, dmg, e.hp <= 0);
  if (o.pid) {
    e.lastPid = o.pid;
    pstats(s, o.pid).damage += dmg;
    // Vela's string: what the hero struck stays smoked.
    relicStruck(s, e, o.pid);
  }
  // A Drowned's grip gives under enough blows.
  if (e.grab) e.cut = (e.cut ?? 0) + dmg;
  // His rage and his channel; a challenged hero striking another nightmare.
  if (e.un) unsleepingHit(s, e, dmg, o.pid);
  else if (s.un && o.pid) unsleepingMarkStrike(s, e, o.pid);
  if (s.dungeon?.sima) simaBossHit(s, e, dmg);
  if (e.anim !== 'stun') {
    e.anim = 'hit';
    e.animT = 0;
  }
  ev(s, { t: 'hit', id: e.id, dmg, x: e.x, y: e.y, ...(o.crit || weak ? { crit: true } : {}), ...(type !== 'physical' ? { dt: type } : {}) });
  // A shining colossus turns the blow back.
  if (e.kind === 'colossus') reflect(s, e, dmg, o.pid);
  // A lidless dreamer struck hard vanishes and comes back a way off.
  const blink = NIGHTMARES[e.kind].blinks;
  if (blink && e.hp > 0 && amount >= blink.at && s.tick >= (e.blinkAt ?? 0)) {
    e.blinkAt = s.tick + blink.cooldown;
    const a = rngInt(s.rng, ANGLE_STEPS);
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 600, kind: 'blink' });
    e.x += idiv(cosA(a) * blink.far, 10000);
    e.y += idiv(sinA(a) * blink.far, 10000);
    e.next = -1;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 600, kind: 'blinkIn' });
  }
  // Bound by shadow chains: a hero's blow hurts the rest of the binding too.
  if (!o.linked && o.pid && (e.linkTicks ?? 0) > 0 && e.linkBp) {
    for (const x of s.enemies) if (x.id !== e.id && x.hp > 0 && x.linkId === e.linkId && (x.linkTicks ?? 0) > 0) damageEnemy(s, x, mulBp(dmg, e.linkBp), { pid: o.pid, type: 'shadow', noReact: true, linked: true });
  }
  // Under a paladin's seal: the hero who struck heals part of the blow.
  if (e.seal && e.seal > 0 && e.sealBp && o.pid) {
    const h = s.heroes.find((x) => x.pid === o.pid && x.downTicks === 0);
    if (h) healHero(s, h, mulBp(dmg, e.sealBp), o.pid);
  }
  if (!o.noReact) react(s, e, type, dmg, o.pid);
  return dmg;
};

/**
 * Reactions between damage types (REACTIONS): frost chills; lightning shatters the chilled; fire sets the
 * poison (or acid) off. Deterministic, like everything here: nearest first, by id.
 */
const react = (s: BattleState, e: Enemy, type: DamageType, dmg: number, pid: PlayerId): void => {
  // The dragon's glass has a cold of its own (battle/dragon.ts): frost never chills it, nor lightning shatters it.
  if (s.dragon && e.id === s.dragon.id) return;
  if (type === 'frost') {
    e.chill = s.tick + REACTIONS.chillTicks;
    return;
  }
  if (type === 'lightning' && (e.chill ?? 0) > s.tick) {
    const r = REACTIONS.shatter;
    e.chill = 0;
    const burst = imax(r.min, mulBp(dmg, r.damageBp));
    for (const o of enemiesNear(s, e.x, e.y, r.radius, (x) => targetable(s, x))) damageEnemy(s, o, burst, { pid, type: 'frost', ignoresArmor: true, noReact: true, shard: true });
    ev(s, { t: 'boom', x: e.x, y: e.y, r: r.radius, kind: 'shatter' });
    return;
  }
  const poisoned = e.dotTicks > 0 && e.dotType === 'poison';
  if (type === 'fire' && (poisoned || (e.acidTicks ?? 0) > 0)) {
    const r = REACTIONS.combust;
    const left = poisoned && e.dotEvery > 0 ? e.dotDmg * idiv(e.dotTicks + e.dotEvery - 1, e.dotEvery) : 0;
    const burst = imax(r.min, mulBp(left, r.damageBp));
    e.dotTicks = 0;
    e.acidTicks = 0;
    e.wasps = undefined;
    for (const o of enemiesNear(s, e.x, e.y, r.radius, (x) => targetable(s, x))) damageEnemy(s, o, burst, { pid, type: 'fire', ignoresArmor: true, noReact: true });
    ev(s, { t: 'boom', x: e.x, y: e.y, r: r.radius, kind: 'combust' });
  }
};

/** A sleeping hero comes to (and can't be put back to sleep for a while). */
export const wake = (s: BattleState, h: Hero): void => {
  h.sleep = 0;
  h.wokeAt = s.tick;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'rouse' });
};

/**
 * A boss's marked blow caught a hero: a live one it catches `AILMENT_MARKS.hits` times in the fight carries
 * its condition for days (docs/CRIATURAS.md §5). A ghost never does; a felling boss's own condition stays.
 */
export const markedBlow = (s: BattleState, h: Hero, kind: AilmentKind): void => {
  if (h.control !== 'live' || s.stats.afflicted?.[h.pid]) return;
  const hits = (s.stats.markedHits ??= {});
  hits[h.pid] = (hits[h.pid] ?? 0) + 1;
  if ((hits[h.pid] ?? 0) >= AILMENT_MARKS.hits) (s.stats.afflicted ??= {})[h.pid] = kind;
};

/** `pierce`: through armour, shields, block and resistances (an Unsleeping's blows); wards and devotion still count. */
export const damageHero = (s: BattleState, h: Hero, amount: number, from?: Enemy | null, kind?: DamageType, pierce = false): void => {
  if (h.downTicks > 0 || h.invuln > 0 || amount <= 0) return;
  // The Chasm's signs on a nightmare's blow: rage below half its health, frost that chills.
  if (from && s.dungeon?.sima) amount = simaBlow(s, h, from, amount);
  // Smoked by Vela: the nightmares' blows (not the Unsleeping's own) land harder.
  if (from && !from.un && (h.unSmoke ?? 0) > 0) amount = mulBp(amount, VELA.smokedTakenBp);
  // An Unsleeping in a saga fight hits at a share of everything she does elsewhere (her blows on a hero's own health too).
  if (from?.un?.shareBp) amount = mulBp(amount, from.un.shareBp);
  // Corvin's gate: a blow from in front lands lighter.
  amount = corvinGuard(h, from, amount);
  // A powder barrel in the hands takes the blow too (La Quemadura 4).
  blastHit(s, h, amount);
  // A paladin's sacrifice nearby takes part of the blow on itself.
  if (!((h.sacrifice ?? 0) > 0)) {
    const p = s.heroes.find((o) => o.id !== h.id && (o.sacrifice ?? 0) > 0 && o.downTicks === 0 && withinRange(o.x, o.y, h.x, h.y, o.sacrificeR ?? 0));
    if (p && p.sacrificeBp) {
      const moved = mulBp(amount, p.sacrificeBp);
      amount -= moved;
      damageHero(s, p, mulBp(moved, p.sacrificeTakenBp ?? 10000), from, kind, pierce);
      if (amount <= 0) return;
    }
  }
  // Anything but a moth wakes a sleeper.
  if (h.sleep && h.sleep > 0 && !(from && NIGHTMARES[from.kind].sleeps)) wake(s, h);
  let dmg = pierce ? amount : mulBp(amount, 10000 - h.stats.armorBp);
  // A shield (docs/EQUIPO.md §2): a melee blow now and then lands for half.
  if (!pierce && from && h.stats.blockBp && rngChance(s.rng, h.stats.blockBp)) dmg = mulBp(dmg, BLOCK_TAKEN_BP);
  // Affinity: the hero's gear guards against the blow's type (or leaves it more open to it).
  const type = kind ?? (from ? NIGHTMARES[from.kind].dmgType : undefined) ?? 'physical';
  if (!pierce) dmg = resisted(dmg, h.stats.resBp?.[type] ?? 0);
  // Iron wall: the challenge hardens the warrior while it lasts.
  if (!pierce && h.taunt > 0 && h.forks?.[1] === 'ironWall') dmg = mulBp(dmg, FORKS.ironWall.takenBp);
  // A cleric's ward (what it spared counts for the dwarf mail's feat).
  if (h.ward && h.ward > 0 && h.wardBp) {
    const was = dmg;
    dmg = mulBp(dmg, h.wardBp);
    if (s.stats.feats) featWard(s, h, was - dmg);
  }
  // A paladin's devotion nearby; its retribution strikes back.
  dmg = mulBp(dmg, devotionBp(s, h));
  if (from) retribution(s, h, from);
  if (dmg < 1) dmg = 1;
  // A paladin's aegis soaks it first (the oath plate taunts when it breaks).
  const shielded = (h.shield ?? 0) > 0;
  const before = dmg;
  if (!pierce) dmg = soak(s, h, dmg, from);
  let taunted = 0;
  if (shielded && !h.shield && h.shieldTaunt) {
    h.shieldTaunt = false;
    const f = UNIQUES.oathPlate.fx;
    const drawn = enemiesNear(s, h.x, h.y, f.radius as number, (o) => o.kind !== 'boneWall');
    for (const e of drawn) {
      taunt(e, h, f.taunt as number);
      taunted++;
    }
    h.taunt = f.taunt as number;
    h.tauntRadius = f.radius as number;
    ev(s, { t: 'boom', x: h.x, y: h.y, r: f.radius as number, kind: 'aegis' });
    // The oath plate's forms: the relay, the steel echo, the steel oath.
    if (h.uqForm) aegisBroke(s, h, drawn);
  }
  if (shielded) featAegis(s, h, before - dmg, taunted);
  if (dmg <= 0) return;
  const ps = pstats(s, h.pid);
  ps.taken = (ps.taken ?? 0) + (dmg < h.hp ? dmg : imax(0, h.hp));
  h.hp -= dmg;
  h.hurtTick = s.tick;
  // A powder barrel in hand takes the blow too (expeditions): struck too often, it goes off.
  if (h.carry !== undefined) {
    const it = s.dungeon?.exp?.items.find((o) => o.id === h.carry);
    if (it?.kind === 'barrel') it.hp -= dmg;
  }
  ev(s, { t: 'hit', id: h.id, dmg, x: h.x, y: h.y, ...(type !== 'physical' ? { dt: type } : {}) });
  // A melee blow bites back by the hero's attack: thorns (rare gear), the last watch while challenging (which also mends) and the dwarf bark in bear form.
  if (from) {
    let bp = h.stats.thornsBp ?? 0;
    const watch = h.uq?.includes('lastWatch') && h.taunt > 0 ? (UNIQUES.lastWatch.fx.attackBp as number) : 0;
    if (watch) {
      bp += watch;
      if (h.hp > 0) healHero(s, h, mulBp(h.stats.maxHp, UNIQUES.lastWatch.fx.healBp as number), h.pid);
    }
    const bark = h.uq?.includes('dwarfBark') && (h.bear ?? 0) > 0 ? (UNIQUES.dwarfBark.fx.attackBp as number) : 0;
    bp += bark;
    const back = mulBp(h.stats.attack, bp);
    if (back > 0) {
      const got = damageEnemy(s, from, back, { pid: h.pid, ignoresArmor: true });
      // What the last watch and the dwarf bark gave back, for their feats (and the last watch's vengeance).
      if (watch || bark) featThorns(s, h, from, idiv(got * watch, bp), idiv(got * bark, bp));
      if (watch && h.uqForm) watchStruckBack(h, idiv(got * watch, bp));
    }
    // The forms that answer a blow: the counterfrost, the bear's hug.
    if (h.uqForm) formsStruck(s, h, from);
  }
  // The forms that keep a hero up: the last turn, unfallen.
  if (h.hp <= 0 && h.uqForm) formsHoldUp(s, h, h.hp + dmg);
  // The postern medal's oath: whoever it reached can't fall while it lasts.
  if (h.hp <= 0 && (h.undying ?? 0) > 0) {
    h.hp = 1;
    featSaved(s, h);
  }
  // Edda's shield: once a fight, untouchable and mended instead of falling.
  if (h.hp <= 0) eddaHolds(s, h);
  if (h.hp <= 0) {
    // Felled live by a boss: the condition it leaves lasts days (a ghost never catches one).
    const ail = from && h.control === 'live' ? (from.kind === 'witch' ? 'ashCurse' : from.kind === 'giant' && from.elite ? 'dread' : null) : null;
    if (ail) (s.stats.afflicted ??= {})[h.pid] = ail;
    heroDown(s, h);
  }
};

/**
 * A hero's basic attack landed for `dealt` (rare gear): lifesteal heals a share of it, and burn sets the
 * target alight — renewed, never weaker than a fire already on it.
 */
export const gearOnHit = (s: BattleState, h: Hero, e: Enemy, dealt: number): void => {
  if (dealt <= 0) return;
  if (h.stats.lifestealBp) healHero(s, h, mulBp(dealt, h.stats.lifestealBp), h.pid);
  // (The dragon's glass never takes a burn: battle/dragon.ts.)
  if (h.stats.burnBp && e.hp > 0 && e.id !== s.dragon?.id) {
    const fx = GEAR_EFFECT_FX.burn;
    const pulse = idiv(mulBp(dealt, h.stats.burnBp), idiv(fx.ticks, fx.every));
    if (pulse > 0 && (e.dotTicks <= 0 || pulse >= e.dotDmg)) {
      e.dotDmg = pulse;
      e.dotType = undefined;
      e.dotEvery = fx.every;
      e.dotTicks = fx.ticks;
    }
  }
};

/** Gives HP back to a standing hero (never over its max); returns what it actually healed. */
export const healHero = (s: BattleState, h: Hero, amount: number, pid: string): number => {
  if (h.downTicks > 0 || amount <= 0) return 0;
  // A mourner's curse: the wounds close slower.
  if ((h.curse ?? 0) > 0 && h.curseBp !== undefined) amount = mulBp(amount, h.curseBp);
  const room = h.stats.maxHp - h.hp;
  const got = amount < room ? amount : room;
  if (got <= 0) return 0;
  h.hp += got;
  const p = pstats(s, pid);
  p.healed = (p.healed ?? 0) + got;
  if (h.pid !== pid) p.aided = (p.aided ?? 0) + got;
  ev(s, { t: 'heal', id: h.id, amount: got, x: h.x, y: h.y });
  return got;
};

/** Wards a hero: it takes `bp` of the damage for `ticks` (the stronger ward wins). */
export const wardHero = (h: Hero, bp: number, ticks: number): void => {
  if (!h.ward || h.ward <= 0 || !h.wardBp || bp < h.wardBp) h.wardBp = bp;
  if ((h.ward ?? 0) < ticks) h.ward = ticks;
};

export const heroDown = (s: BattleState, h: Hero): void => {
  // Under a cleric's intercession: straight back up instead.
  if ((h.intercede ?? 0) > 0) {
    h.intercede = 0;
    h.hp = imax(1, mulBp(h.stats.maxHp, h.intercedeBp ?? 5000));
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 800, kind: 'intercede' });
    return;
  }
  h.hp = 0;
  h.downTicks = h.relics.includes('dreamerRing') ? (awake(h, 'dreamerRing') ? RELIC_AWAKENED_FX.dreamerRing.respawnTicks : RESPAWN_TICKS_RING) : RESPAWN_TICKS;
  h.target = 0;
  h.forced = false;
  h.moving = false;
  h.path = [];
  h.taunt = 0;
  h.anim = 'down';
  h.animT = 0;
  pstats(s, h.pid).downs++;
  s.stats.highlights.push({ tick: s.tick, kind: 'heroDown', pid: h.pid });
  ev(s, { t: 'down', id: h.id });
  // The medal's oath that goes off by itself, and what a form gathered for this life.
  formsDown(s, h);
};

export const respawnHero = (s: BattleState, h: Hero): void => {
  const home = heroHome(s, h.post);
  h.x = home.x;
  h.y = home.y;
  h.destX = home.x;
  h.destY = home.y;
  h.ax = home.x;
  h.ay = home.y;
  h.hp = h.stats.maxHp;
  h.anim = 'idle';
  h.animT = 0;
  h.stun = 0;
  ev(s, { t: 'up', id: h.id });
};

/** Sector a cell belongs to (closest gate). */
export const sectorOf = (s: BattleState, q: number, r: number): number => {
  let best = 0;
  let bd = 0x7fffffff;
  for (const sec of s.map.sectors) {
    const d = hexDist(sec.gate, { q, r });
    if (d < bd) {
      bd = d;
      best = sec.index;
    }
  }
  return best;
};

export const damageStructure = (s: BattleState, st: Structure, amount: number, from?: Enemy | null, kind?: DamageType): void => {
  if (st.destroyed || amount <= 0 || st.maxHp <= 0) return;
  // By material: wood burns, acid eats stone, iron holds against both.
  const type = kind ?? (from ? NIGHTMARES[from.kind].dmgType : undefined) ?? 'physical';
  const mat = MATERIAL_RESIST[st.material as 1 | 2 | 3]?.[type] ?? 0;
  if (mat !== 0) amount = imax(1, mulBp(amount, 10000 - mat));
  // Iron cladding shrugs off part of every blow.
  if (st.material === 3) {
    amount = mulBp(amount, IRON_DAMAGE_TAKEN_BP);
    if (amount < 1) amount = 1;
  }
  // The temple, with a paladin fighting; a repair drone's armor plates.
  amount = mulBp(amount, templeGuardBp(s, st));
  amount = mulBp(amount, droneCoverBp(s, st));
  amount = mulBp(amount, rampartBp(s, st));
  // Bruni's hammer carried near it.
  amount = mulBp(amount, bruniBp(s, st));
  // The dream mortar offered at the keep.
  if (s.rules?.structureTakenBp) amount = mulBp(amount, s.rules.structureTakenBp);
  // Era V's titan stone: the ring wall and its gates shrug off much of every blow.
  // Era V's titan stone: the ring and every gate, and on terraced land the group's own iron wall runs too.
  if ((st.type === 'rampart' || st.type === 'gate' || (st.type === 'wall' && st.material === 3 && !!s.map.land)) && mapEra(s.map) >= 5) amount = mulBp(amount, TITAN_STONE.takenBp);
  // Cracked by a frost colossus: brittle for a while.
  if ((st.brittleUntil ?? 0) > s.tick) amount = mulBp(amount, NIGHTMARES.frostColossus.brittles?.takenBp ?? 10000);
  if (amount < 1) amount = 1;
  amount = soak(s, st, amount, from);
  if (amount <= 0) return;
  st.hp -= amount;
  const c = hexToWorld(st.q, st.r);
  ev(s, { t: 'hit', id: st.id, dmg: amount, x: c.x, y: c.y });
  if (st.hp <= 0) {
    st.hp = 0;
    st.destroyed = true;
    st.cursed = false;
    touchGrid(s);
    const sector = sectorOf(s, st.q, st.r);
    const ss = s.stats.sectors[sector];
    if (ss) {
      ss.lost++;
      if (st.type === 'gate') ss.gateFell = true;
    }
    s.stats.highlights.push({
      tick: s.tick,
      kind: st.type === 'gate' ? 'gateFell' : 'structureLost',
      sector,
      what: st.type,
    });
    ev(s, { t: 'struct', id: st.id, what: 'destroyed' });
  }
};

export const damageKeep = (s: BattleState, amount: number, e: Enemy | null): void => {
  if (amount <= 0) return;
  // A Great Work under way leaves the keep frailer.
  if (!s.objective && s.rules?.keepFrailBp) amount = mulBp(amount, s.rules.keepFrailBp);
  if (!s.objective) {
    // A paladin standing guard by it, and an aegis raised on it.
    amount = mulBp(amount, keepGuardBp(s));
    if (s.keepShield && s.keepShield > 0 && s.keepShieldTicks && s.keepShieldTicks > 0) {
      const took = amount < s.keepShield ? amount : s.keepShield;
      s.keepShield -= took;
      amount -= took;
      if (amount <= 0) return;
    }
  }
  const target = s.objective ?? s.keep;
  target.hp -= amount;
  if (target.hp < 0) target.hp = 0;
  if (s.objective) s.keep.hp = s.objective.hp;
  s.stats.keepDamage += amount;
  if (e && !e.leaked) {
    e.leaked = true;
    const ss = s.stats.sectors[e.sector];
    if (ss) ss.leaks++;
  }
  ev(s, { t: 'keep', dmg: amount });
};

export const enemyMaxHp = (kind: EnemyKind, tier: 1 | 2 | 3, elite: boolean): number => {
  let hp = mulBp(NIGHTMARES[kind].hp, TIERS[tier].hpBp);
  if (elite) hp = mulBp(hp, ELITE_HP_BP);
  return hp;
};

export const spawnEnemy = (
  s: BattleState,
  kind: EnemyKind,
  tier: 1 | 2 | 3,
  elite: boolean,
  sector: number,
  x: number,
  y: number,
  hpOverride = 0,
): Enemy => {
  const d = NIGHTMARES[kind];
  // A holiday season makes every nightmare tougher (split halves already carry it in their parent's HP).
  const season = s.season ? SEASON_BY_ID[s.season] : null;
  const maxHp = hpOverride > 0 ? hpOverride : season ? mulBp(enemyMaxHp(kind, tier, elite), season.hpBp) : enemyMaxHp(kind, tier, elite);
  const e: Enemy = shapeEnemy({
    id: s.nextId++,
    kind,
    tier,
    elite,
    x,
    y,
    hp: maxHp,
    maxHp,
    speed: mulBp(d.speed, TIERS[tier].speedBp),
    damage: mulBp(elite ? mulBp(d.damage, 15000) : d.damage, season ? season.damageBp : 10000),
    reload: d.reload,
    cd: 0,
    sector,
    lane: d.speed === 0 ? 0 : rngRange(s.rng, -280, 280),
    next: -1,
    moveAcc: 0,
    tgtKind: 0,
    tgtId: 0,
    stun: 0,
    slowBp: 0,
    slowTicks: 0,
    dotDmg: 0,
    dotEvery: 0,
    dotTicks: 0,
    lastHit: -1000,
    regenAcc: 0,
    taunter: 0,
    tauntTicks: 0,
    split: false,
    phase: 0,
    timer: 0,
    facing: 0,
    anim: 'walk',
    animT: 0,
    lastPid: '',
    born: s.tick,
    trapCell: -1,
    leaked: false,
    _cell: -2,
    _cx: x,
    _cy: y,
  });
  // A blood moon: every nightmare hits harder.
  if (s.rules?.enemyDamageBp) e.damage = mulBp(e.damage, s.rules.enemyDamageBp);
  // The Chasm: the floor's health and blows on everything that comes out down there (docs/SIMA.md).
  if (s.dungeon?.sima) simaSpawned(s, e, hpOverride > 0);
  s.enemies.push(e);
  ev(s, { t: 'spawn', id: e.id });
  return e;
};

export const findEnemy = (s: BattleState, id: number): Enemy | null => {
  if (id <= 0) return null;
  // Enemies are sorted by id: binary search.
  const a = s.enemies;
  let lo = 0;
  let hi = a.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const e = a[mid] as Enemy;
    if (e.id === id) return e;
    if (e.id < id) lo = mid + 1;
    else hi = mid - 1;
  }
  return null;
};

export const findHero = (s: BattleState, id: number): Hero | null => {
  for (const h of s.heroes) if (h.id === id) return h;
  return null;
};

export const findStructure = (s: BattleState, id: number): Structure | null => {
  for (const st of s.structures) if (st.id === id) return st;
  return null;
};

export const structurePos = (st: Structure): { x: number; y: number } => hexToWorld(st.q, st.r);

/** Enemies within radius of a point, in id order. */
export const enemiesNear = (s: BattleState, x: number, y: number, r: number, filter?: (e: Enemy) => boolean): Enemy[] => {
  const out: Enemy[] = [];
  const r2 = r * r;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    if (dist2(e.x, e.y, x, y) > r2) continue;
    if (filter && !filter(e)) continue;
    out.push(e);
  }
  return out;
};

/**
 * How many enemies `enemiesNear` would return, without building the list; with `upTo` it stops counting
 * there (for "at least n" checks). The filter must only read: it is not asked about the ones past `upTo`.
 */
export const countNear = (s: BattleState, x: number, y: number, r: number, filter?: (e: Enemy) => boolean, upTo = 0x7fffffff): number => {
  let n = 0;
  const r2 = r * r;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    if (dist2(e.x, e.y, x, y) > r2) continue;
    if (filter && !filter(e)) continue;
    if (++n >= upTo) return n;
  }
  return n;
};

/** The first enemy `enemiesNear` would return (lowest id), or undefined; the filter must only read. */
export const firstNear = (s: BattleState, x: number, y: number, r: number, filter?: (e: Enemy) => boolean): Enemy | undefined => {
  const r2 = r * r;
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    if (dist2(e.x, e.y, x, y) > r2) continue;
    if (filter && !filter(e)) continue;
    return e;
  }
  return undefined;
};

/** Distance-to-keep for "first" targeting: ground uses the flow field, flyers straight line. */
export const progressKey = (s: BattleState, e: Enemy, flow: Int32Array): number => {
  if (isFlying(e)) return idiv(imax(Math.abs(e.x), Math.abs(e.y)), 100);
  const id = enemyCell(s, e);
  const f = id >= 0 ? (flow[id] as number) : -1;
  return f < 0 ? 0x7ffffff : f;
};

export const cellIdAt = (s: BattleState, x: number, y: number): number => worldToCell(s.map.radius, x, y);

/** The cell an enemy stands on: `cellIdAt` of its position, worked out again only once it has moved. */
export const enemyCell = (s: BattleState, e: Enemy): number => {
  if (e._cell !== undefined && e._cell !== -2 && e._cx === e.x && e._cy === e.y) return e._cell;
  const c = worldToCell(s.map.radius, e.x, e.y);
  e._cell = c;
  e._cx = e.x;
  e._cy = e.y;
  return c;
};

export { cellCenter };

/** Adds a sample to a fixed-bucket histogram. */
export const histAdd = (hist: number[], value: number, width: number): void => {
  let b = idiv(value, width);
  if (b < 0) b = 0;
  if (b >= hist.length) b = hist.length - 1;
  hist[b] = (hist[b] as number) + 1;
};
